import { TextareaInputField } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MarginDecisionEnum } from "@/graphql/__generated__/graphql";
import { useSubmitMarginDecisionMutationHook } from "@/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { PiGavelFill } from "react-icons/pi";
import { JSX, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { CircleLoader } from "@/components/shared/loaders";

const createMarginDecisionOverruleSchema = () => {
  return z
    .object({
      decision: z.enum([MarginDecisionEnum.OverruleApprove, MarginDecisionEnum.OverruleReject]),
      comment: z.string().optional(),
    });
};

/**
 * MarginDecisionOverruleDialog is a React functional component that renders a dialog for overruling a decision.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <MarginDecisionOverruleDialog />
 */
function MarginDecisionOverruleDialog({hasFinalDecision}: {hasFinalDecision: boolean}): JSX.Element {
  const { t }  = useTranslation(["modals", "common"]);
  const { marginDecisionGroupID: marginDecisionGroupId } = useParams<{ marginDecisionGroupID: string, tenderID: string }>();

  const [open, setOpen] = useState(false);

  const { submitMarginDecisionMutation, loading } = useSubmitMarginDecisionMutationHook();
  const MarginDecisionOverruleSchema = createMarginDecisionOverruleSchema();
  const form = useForm<z.infer<typeof MarginDecisionOverruleSchema>>({
    resolver: zodResolver(MarginDecisionOverruleSchema),
    mode: "onChange",
  });

  const onSubmit = (data: z.infer<typeof MarginDecisionOverruleSchema>) => {
    submitMarginDecisionMutation({
      variables: {
        marginDecisionGroupId: Number(marginDecisionGroupId),
        decision: data.decision,
        comment: data.comment,
      },
      onCompleted: () => {
        setOpen(false);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          disabled={hasFinalDecision}
          onClick={() => {
            setOpen(true);
            form.reset();
          }}
        >
          <PiGavelFill className="icon-xs mr-2" />
          {t("common:buttons.overrule")}
        </Button>
      </DialogTrigger>
      <Form {...form}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("margindecisionoverruledialog.title")}</DialogTitle>
            <DialogDescription>
              {t("margindecisionoverruledialog.description")}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-col-4">
            <FormField
              control={form.control}
              name="decision"
              render={({ field }) => (
                <FormItem>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("margindecisionoverruledialog.inputs.decision.placeholder")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {[MarginDecisionEnum.OverruleApprove, MarginDecisionEnum.OverruleReject].map((type) => (
                        <SelectItem key={type} value={type}>
                          {t("common:margindecisionenum." + type.toLowerCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <TextareaInputField
              form={form}
              name="comment"
              label={t("margindecisionoverruledialog.inputs.comment.label")}
              placeholder={t("margindecisionoverruledialog.inputs.comment.placeholder")}
              className="w-full"
              showOptional
              isFormItem={false}
            />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant={"outline"}>
                  {t("common:buttons.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={loading} className="gap-2">
                <CircleLoader visible={loading} height={16} width={16} strokeWidth={2}/>
                {t("common:buttons.overrule")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Form>
    </Dialog>
  );
}

export default MarginDecisionOverruleDialog;
